import { apiFetch } from "./client";
import type { CompanyAddress, CompanyAddressBody, Page } from "./admin";

export type OrderStatus =
  | "DRAFT"
  | "SUBMITTED"
  | "IN_REVIEW"
  | "APPROVED"
  | "REJECTED"
  | "SHIPPED"
  | "INVOICED"
  | "CANCELLED";

/** One point on the order's status timeline. `changedByName` is null for system rows. */
export type StatusHistoryEntry = {
  status: OrderStatus;
  changedAt: string;
  changedByName: string | null;
};

/** How far the order has been delivered. Derived server-side from the lines' dispatched
 *  quantities — never from the presence of a delivery document, which the ERP links for
 *  only a small share of orders. Never recompute this in the UI. */
export type FulfillmentState = "NOT_SHIPPED" | "PARTIALLY_SHIPPED" | "FULLY_SHIPPED";

/** One dispatched article on a delivery document. `orderLineId`, `orderLinePosition` and
 *  `unit` are null together when the ERP item does not resolve to a portal line — still a
 *  real dispatch, shown at document level rather than under a line. */
export type ShipmentLine = {
  id: string;
  orderLineId: string | null;
  orderLinePosition: number | null;
  sku: string | null;
  description: string | null;
  quantity: number;
  unit: string | null;
};

/** One ERP delivery document as it applies to this order. */
export type Shipment = {
  id: string;
  /** Human delivery-note number, e.g. `26-R90-000506`. */
  number: string;
  /** ERP document type — R900 / BO00 / the invoice types 1100, 1200, 1300. */
  docType: string | null;
  shippedAt: string | null;
  receiver: string | null;
  note: string | null;
  lines: ShipmentLine[];
};

/** Order-level delivery progress. The counts are per line, not per quantity — an order's
 *  lines can carry different units. `shipments` is often empty even for a delivered order. */
export type OrderInvoice = {
  number: string;
  issueDate: string | null;
  dueDate: string | null;
  grossAmount: number | null;
  currency: string | null;
  status: string | null;
  /** Past due on the finance mirror's own reference date. Server-derived — never `due < today`
   *  in the browser: the ERP snapshot is frozen, and the calendar would redden everything. */
  overdue: boolean;
};

export type Fulfillment = {
  state: FulfillmentState;
  lineCount: number;
  fullyShippedLineCount: number;
  /** Delivery documents, oldest first. */
  /** Warehouse AB00 dispatches — the server sends these to IMP-internal callers ONLY;
   *  a customer receives an empty list, not a hidden section. */
  shipments: Shipment[];
  /** The invoices billing this order — what the customer actually holds. Money fields are
   *  null on an invoice that aged out of the finance mirror's 36-month window. */
  invoices: OrderInvoice[];
};

export type OrderLine = {
  id: string;
  position: number;
  productId: string | null;
  productSku: string | null;
  productName: string | null;
  /** The product's mandatory purchase quantity, or null — see `ProductSummary.minOrderQty`.
   *  Sent with the line so a quantity below it can be flagged without a catalogue call. */
  minOrderQty?: number | null;
  sparePartId: string | null;
  sparePartSku: string | null;
  sparePartName: string | null;
  /** The ERP article code the document carried, whether or not it resolved to a catalogue row.
   *  The last resort for a line's identity; null on portal-created lines. */
  erpIdent: string | null;
  freeTextDescription: string | null;
  quantity: number;
  unit: string;
  /** Dispatched so far, mirrored from the ERP. May exceed `quantity` — over-dispatch is real. */
  shippedQuantity: number;
  /** `quantity - shippedQuantity`, floored at zero by the API. */
  remainingQuantity: number;
  customerNotes: string | null;
  catalogUnitPrice: number | null;
  currency: string | null;
  contractedDiscountPct: number;
  finalDiscountPct: number;
  discountReason: string | null;
  netUnitPrice: number | null;
  lineTotal: number | null;
  priceSource?: PriceSource;
  /**
   * Presigned image for this line's product, sent WITH the line. Do not fall back to
   * looking the product up in a client-side catalogue list — that is what made lines
   * beyond the first 2,000 products render a placeholder.
   */
  productThumbnailUrl: string | null;
};

/** Frozen address snapshot shown on the order/invoice. `recipientName` is null
 *  for billing-only views; `vat` is null for delivery-only views. */
export type OrderAddress = {
  name: string | null;
  recipientName: string | null;
  line1: string | null;
  line2: string | null;
  city: string | null;
  postal: string | null;
  country: string | null;
  vat: string | null;
};

/** What an order gets for each purpose when its picker is left on "Company default": the
 *  address-book row flagged default, else the company's registered address. A side is null
 *  only when even the company record carries nothing to show. */
export type DefaultCompanyAddresses = {
  delivery: OrderAddress | null;
  billing: OrderAddress | null;
};

export type Order = {
  id: string;
  companyId: string;
  companyName: string;
  companyCountry: string | null;
  orderNumber: string;
  /**
   * The PANTHEON document number (26-N10-002288) once the order is there — assigned by the ERP on
   * approval, never before. Shown as THE number from then on; orderNumber is the portal's own
   * provisional reference until then.
   */
  pantheonKey?: string | null;
  status: OrderStatus;
  requestedDeliveryDate: string | null;
  customerNotes: string | null;
  salesNotes: string | null;
  customerReference: string | null;
  createdBy: string | null;
  submittedAt: string | null;
  createdAt: string;
  updatedAt: string;
  lines: OrderLine[];
  orderTotal: number;
  currency: string;
  moq: number | null;
  belowMoq: boolean;
  statusHistory: StatusHistoryEntry[];
  /** Frozen delivery address snapshot (IMP-13), or null when none captured. */
  deliveryAddress: OrderAddress | null;
  /** Frozen billing address snapshot (IMP-14), or null when none captured. */
  billingAddress: OrderAddress | null;
  /** Delivery progress + the known shipment documents; null when the caller didn't resolve it. */
  fulfillment: Fulfillment | null;
  /**
   * What was agreed when the order was written — the customer's standing terms unless whoever
   * wrote it overrode them. A copy, not a reference: changing the customer's defaults must not
   * rewrite orders already sent. Set on create only; the update endpoint does not carry them.
   *
   * Optional here like `pantheonKey`: the server always sends both keys, and the `?` only spares
   * every Order fixture in the suite from restating them.
   */
  paymentMethod?: string | null;
  paymentDays?: number | null;
};

/** Lightweight order-list row (no line detail) — matches the backend OrderSummaryView.
 *  Used by the all-orders list/board and the dashboards; the full {@link Order} is only
 *  fetched for a single order's detail and for draft-cart flows that need the lines. */
export type OrderSummary = {
  id: string;
  companyId: string;
  companyName: string;
  companyCountry: string | null;
  orderNumber: string;
  /** The PANTHEON document number once the order is there; shown as THE number from then on. */
  pantheonKey?: string | null;
  status: OrderStatus;
  requestedDeliveryDate: string | null;
  submittedAt: string | null;
  createdAt: string;
  lineCount: number;
  orderTotal: number;
  currency: string;
  moq: number | null;
  belowMoq: boolean;
  updatedAt: string | null;
};

/**
 * Where a line's catalog price came from. Present ONLY for IMP staff — the server omits the key
 * for customers, so treat `undefined` as "not for you", not as "unknown".
 * PRODUCT_LIST_PRICE is the one to flag: the customer's list had no row and the product's own
 * list price was used instead.
 */
export type PriceSource = "PRICE_LIST" | "PRODUCT_LIST_PRICE" | "NONE" | "ERP";

export type QuoteLine = {
  productId: string;
  quantity: number;
  catalogUnitPrice: number | null;
  contractedDiscountPct: number | null;
  netUnitPrice: number | null;
  lineTotal: number | null;
  priceSource?: PriceSource;
};

export type QuoteResult = {
  lines: QuoteLine[];
  orderTotal: number;
  currency: string;
  showDiscount: boolean;
};

export type LineInput = {
  productId?: string | null;
  sparePartId?: string | null;
  freeTextDescription?: string | null;
  quantity: number;
  unit: string;
  customerNotes?: string | null;
};

const q = (params: Record<string, string | undefined>) => {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => v && usp.set(k, v));
  const s = usp.toString();
  return s ? `?${s}` : "";
};

export type OrderReservation = {
  id: string; erpId: number | null; sku: string; warehouse: string; quantity: number;
  reservedByName: string | null; reservedAt: string | null; orderDocNo: string | null;
};

/**
 * The number people call the order by: the PANTHEON document number once the order is there,
 * the portal's provisional reference before that. One helper so the list, the board and the
 * editor never disagree about which number is THE number (owner, 2026-09-10).
 */
export function displayOrderNumber(o: { orderNumber: string; pantheonKey?: string | null }): string {
  return o.pantheonKey ?? o.orderNumber;
}

/**
 * What a RELEASE answers, which is not what a reserve answers.
 *
 * The server builds it as `new ReservationView(null, null, ident, null, quantity, …)` — there is no
 * row left to carry an id or a warehouse, and `quantity` is null when the whole hold went. Typing it
 * as OrderReservation would read those as non-null and print "Released null x" on the common path,
 * with nothing for the compiler to catch.
 */
export type ReleasedHold = {
  id: null; erpId: null; sku: string; warehouse: null; quantity: number | null;
  reservedByName: string | null; reservedAt: string | null; orderDocNo: string | null;
};

export const ordersApi = {
  /**
   * One page of full orders (lines, shipments, prices), newest first. The server clamps `size`
   * to 200 — as a bare list this was every order the caller could see: 169 MB, cut by nginx at
   * 60 s. Use `totalElements` to tell the reader when what they see is not all there is.
   */
  listPage: (params: { companyId?: string; status?: OrderStatus; page?: number; size?: number } = {}) =>
    apiFetch<Page<Order>>(
      `/orders${q({
        companyId: params.companyId,
        status: params.status,
        page: params.page === undefined ? undefined : String(params.page),
        size: params.size === undefined ? undefined : String(params.size),
      })}`,
    ),
  /** The first page only — for callers that want "the draft(s)" or "this company's recent orders". */
  list: (params: { companyId?: string; status?: OrderStatus } = {}) =>
    ordersApi.listPage(params).then((p) => p.content),
  /** Lightweight list for the orders page/board/dashboards — no line detail, one aggregate query. */
  listSummary: (params: { companyId?: string; status?: OrderStatus } = {}) =>
    apiFetch<OrderSummary[]>(`/orders/summary${q(params)}`),
  get: (id: string) => apiFetch<Order>(`/orders/${id}`),
  create: (body: {
    companyId: string;
    requestedDeliveryDate?: string;
    customerNotes?: string;
    customerReference?: string;
    lines?: LineInput[];
    deliveryAddressId?: string | null;
    billingAddressId?: string | null;
    /** Omitting both takes the customer's standing terms; a value overrides them for this order. */
    paymentMethod?: string | null;
    paymentDays?: number | null;
  }) => apiFetch<Order>("/orders", { method: "POST", body }),
  update: (
    id: string,
    body: Partial<Pick<Order, "requestedDeliveryDate" | "customerNotes" | "salesNotes" | "customerReference">> & {
      deliveryAddressId?: string | null;
      billingAddressId?: string | null;
      /** True re-snapshots the company default and clears the picked row. The id fields
       *  cannot say it: null there means "unchanged" — which is how ORD-202609-0003 kept a
       *  row its owner had switched away from (2026-09-10). */
      useDefaultDeliveryAddress?: boolean;
      useDefaultBillingAddress?: boolean;
    },
  ) => apiFetch<Order>(`/orders/${id}`, { method: "PUT", body }),
  // Customer-reachable address book for the order pickers — gated by company
  // access (own company / sales-assigned / admin), not companies:edit.
  listCompanyAddresses: (companyId: string) =>
    apiFetch<CompanyAddress[]>(`/orders/companies/${companyId}/addresses`),
  // The resolved "Company default" for each picker, so the option can name it. Same gate.
  defaultCompanyAddresses: (companyId: string) =>
    apiFetch<DefaultCompanyAddresses>(`/orders/companies/${companyId}/default-addresses`),
  addCompanyAddress: (companyId: string, body: CompanyAddressBody) =>
    apiFetch<CompanyAddress>(`/orders/companies/${companyId}/addresses`, {
      method: "POST",
      body,
    }),
  quote: (companyId: string, lines: { productId?: string | null; sparePartId?: string | null; quantity: number }[]) =>
    apiFetch<QuoteResult>("/orders/quote", { method: "POST", body: { companyId, lines } }),
  /** Hold stock in PANTHEON against one line — staff only; the order must already be there. */
  reserve: (orderId: string, lineId: string, body: { site: string; quantity: number }) =>
    apiFetch<OrderReservation>(`/orders/${orderId}/lines/${lineId}/reserve`, { method: "POST", body }),
  // Omit quantity to release the whole hold. The server reads it as a query parameter, not a body:
  // DELETE with a body is legal but proxies and clients drop it, which would silently turn a
  // partial release into a full one.
  release: (orderId: string, lineId: string, quantity?: number) =>
    apiFetch<ReleasedHold>(
      `/orders/${orderId}/lines/${lineId}/reserve`
        + (quantity == null ? "" : `?quantity=${encodeURIComponent(String(quantity))}`),
      { method: "DELETE" }),
  addLine: (id: string, body: LineInput) =>
    apiFetch<Order>(`/orders/${id}/lines`, { method: "POST", body }),
  removeLine: (id: string, lineId: string) =>
    apiFetch<Order>(`/orders/${id}/lines/${lineId}`, { method: "DELETE" }),
  submit: (id: string) => apiFetch<Order>(`/orders/${id}/submit`, { method: "POST" }),
  transition: (id: string, to: OrderStatus, salesNotes?: string) =>
    apiFetch<Order>(`/orders/${id}/transition`, { method: "POST", body: { to, salesNotes } }),
  applyPricing: (
    id: string,
    lines: { lineId: string; finalDiscountPct: number; reason?: string | null }[],
  ) => apiFetch<Order>(`/orders/${id}/pricing`, { method: "PUT", body: { lines } }),
  clone: (id: string) => apiFetch<Order>(`/orders/${id}/clone`, { method: "POST" }),
  stats: (params: { year?: number; compareYear?: number; companyId?: string } = {}) =>
    apiFetch<OrderStats>(
      `/orders/stats${q({
        year: params.year?.toString(),
        compareYear: params.compareYear?.toString(),
        companyId: params.companyId,
      })}`,
    ),
};

export type YearStats = {
  year: number;
  total: number;
  monthlyCounts: number[];
  byStatus: Record<string, number>;
  totalQuantity: string; // BigDecimal serialised
};

export type OrderStats = {
  current: YearStats;
  compare: YearStats | null;
};

export function nextAllowed(from: OrderStatus): OrderStatus[] {
  switch (from) {
    // Deliberately NOT a full mirror of the backend FSM, which also allows DRAFT -> APPROVED.
    // That edge exists for one thing — a staff member approving an order IMP wrote — and it has
    // its own CTA, which knows whether the caller is staff. Listing it here too would put
    // "Submit for review" and "Approve" side by side on the same draft, and offer Approve on a
    // CUSTOMER's draft, which the server refuses (CUSTOMER_DRAFT_NOT_SUBMITTED). This list drives
    // affordances, not authorization; the server is the authority either way.
    case "DRAFT":          return ["SUBMITTED", "CANCELLED"];
    case "SUBMITTED":      return ["IN_REVIEW", "CANCELLED"];
    case "IN_REVIEW":      return ["APPROVED", "REJECTED", "CANCELLED"];
    case "APPROVED":       return ["SHIPPED", "CANCELLED"];
    case "SHIPPED":        return ["INVOICED"];
    default:               return [];
  }
}

export function fulfillmentBadgePalette(state: FulfillmentState): { bg: string; fg: string } {
  switch (state) {
    case "NOT_SHIPPED":       return { bg: "#f3f4f6", fg: "#6b7280" };
    case "PARTIALLY_SHIPPED": return { bg: "#dbeafe", fg: "#1d4ed8" };
    case "FULLY_SHIPPED":     return { bg: "#dcfce7", fg: "#166534" };
  }
}

export function statusBadgePalette(status: OrderStatus): { bg: string; fg: string } {
  switch (status) {
    case "DRAFT":          return { bg: "#f3f4f6", fg: "#6b7280" };
    case "SUBMITTED":      return { bg: "#dbeafe", fg: "#1d4ed8" };
    case "IN_REVIEW":      return { bg: "#fef3c7", fg: "#a16207" };
    case "APPROVED":       return { bg: "#cffafe", fg: "#0e7490" };
    case "REJECTED":       return { bg: "#fee2e2", fg: "#b91c1c" };
    case "SHIPPED":        return { bg: "#e0e7ff", fg: "#3730a3" };
    case "INVOICED":       return { bg: "#dcfce7", fg: "#166534" };
    case "CANCELLED":      return { bg: "#fef2f2", fg: "#991b1b" };
  }
}
